import "./Login.css";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { auth, db } from "../firebase";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { Mail, Lock, ArrowRight, ShieldCheck, Store } from "lucide-react";

function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const clearStoreSession = () => {
    localStorage.removeItem("storeId");
    localStorage.removeItem("storeName");
    localStorage.removeItem("storeUid");
    localStorage.removeItem("storeEmail");
  };

  const blockLogin = async (message) => {
    clearStoreSession();

    try {
      await signOut(auth);
    } catch (error) {
      console.error("Sign out error:", error);
    }

    setErrorMsg(message);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!email.trim() || !password.trim()) {
      setErrorMsg("Please enter email and password.");
      return;
    }

    try {
      setLoading(true);
      clearStoreSession();

      const userCred = await signInWithEmailAndPassword(
        auth,
        email.trim().toLowerCase(),
        password
      );

      const uid = userCred.user.uid;

      const storeRef = doc(db, "stores", uid);
      const storeSnap = await getDoc(storeRef);

      if (!storeSnap.exists()) {
        await blockLogin("Store profile not found. Please contact PointsHub admin.");
        return;
      }

      const storeData = storeSnap.data();

      if (storeData.isDeleted === true) {
        await blockLogin("This store account has been deleted. Please contact PointsHub admin.");
        return;
      }

      if (storeData.isActive !== true) {
        await blockLogin("This store account is suspended. Please contact PointsHub admin.");
        return;
      }

      const storeId = storeData.storeId || storeData.StoreId;
      const storeName = storeData.storeName || storeData.StoreName || "Store";

      if (!storeId) {
        await blockLogin("Store ID is missing. Please contact PointsHub admin.");
        return;
      }

      localStorage.setItem("storeId", storeId);
      localStorage.setItem("storeName", storeName);
      localStorage.setItem("storeUid", uid);
      localStorage.setItem("storeEmail", storeData.email || email.trim().toLowerCase());

      navigate("/Home", { replace: true });
    } catch (err) {
      console.error("LOGIN ERROR:", err);
      clearStoreSession();

      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password") {
        setErrorMsg("Invalid email or password. Please try again.");
      } else if (err.code === "auth/invalid-credential") {
        setErrorMsg("Invalid email or password. Please try again.");
      } else if (err.code === "auth/too-many-requests") {
        setErrorMsg("Too many login attempts. Please try again later.");
      } else {
        setErrorMsg("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg-circle circle-one"></div>
      <div className="login-bg-circle circle-two"></div>

      <div className="login-shell">
        <div className="login-left">
          <div className="brand-block">
            <div className="brand-logo-wrap">
              <img
                src="/logo.png"
                alt="PointsHub Logo"
                className="brand-logo"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="brand-fallback">
                <Store size={34} />
              </div>
            </div>

            <div>
              <h1>PointsHub</h1>
              <p>Store Terminal</p>
            </div>
          </div>

          <div className="hero-content">
            <span className="hero-badge">
              <ShieldCheck size={18} />
              Secure Store Access
            </span>

            <h2>Manage customer loyalty points with confidence.</h2>

            <p>
              Sign in to your store dashboard to swipe cards, add members,
              check balances, and manage store-specific transactions.
            </p>
          </div>

          <div className="feature-row">
            <div>
              <h4>Store Safe</h4>
              <p>Each store only sees its own customers and transactions.</p>
            </div>
            <div>
              <h4>Fast Checkout</h4>
              <p>Swipe, confirm, add or redeem points in seconds.</p>
            </div>
          </div>
        </div>

        <div className="login-right">
          <form className="login-card" onSubmit={handleLogin}>
            <div className="login-card-header">
              <h2>Welcome Back</h2>
              <p>Sign in to continue to your store terminal.</p>
            </div>

            {errorMsg && <div className="login-error">{errorMsg}</div>}

            <div className="input-group-custom">
              <label>Email Address</label>
              <div className="input-box">
                <Mail size={20} />
                <input
                  type="email"
                  placeholder="store@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="input-group-custom">
              <label>Password</label>
              <div className="input-box">
                <Lock size={20} />
                <input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? "Signing In..." : "Sign In"}
              {!loading && <ArrowRight size={20} />}
            </button>

            <p className="login-note">
              Access is restricted to registered PointsHub store accounts.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;